import { useState, useEffect } from 'react';
import API from '@/lib/api';
import { X, TrendUp, TrendDown, CheckCircle, Warning, Calculator, ArrowRight } from '@phosphor-icons/react';

const fmt = (n) => `€ ${Number(n || 0).toFixed(2).replace('.', ',')}`;

/**
 * Prijs-verhogingswizard. Past prijzen aan met +X% / -X% op geselecteerde prijsgroepen,
 * met filters (categorie/leverancier), afrondingsopties en marge-bescherming.
 * Genereert een CSV die door de AI Import-modal kan worden gereviewd.
 */
export default function PrijsWizardModal({ open, onClose, onStuurNaarImport }) {
  const [stap, setStap] = useState(1); // 1=configuratie, 2=preview, 3=resultaat
  const [percentage, setPercentage] = useState(2.5);
  const [richting, setRichting] = useState('op'); // op | neer
  const [groepenAanpassen, setGroepenAanpassen] = useState(['basic']);
  const [filterCategorie, setFilterCategorie] = useState('');
  const [filterLeverancier, setFilterLeverancier] = useState('');
  const [afronding, setAfronding] = useState('geen');
  const [minMarge, setMinMarge] = useState(0);
  const [inclAankoop, setInclAankoop] = useState(false);
  // Ontbrekende prijsgroepen automatisch afleiden uit basic
  const [afleidenActief, setAfleidenActief] = useState(true);
  const [fallbackVan, setFallbackVan] = useState('basic');
  const [fallbackFactoren, setFallbackFactoren] = useState({ standard: 95, premium: 110, bulk: 85, kassa: 100 });
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [categorieen, setCategorieen] = useState([]);
  const [leveranciers, setLeveranciers] = useState([]);
  const [prijsgroepen, setPrijsgroepen] = useState([]);

  useEffect(() => {
    if (!open) return;
    // Laad beschikbare filters + prijsgroepen
    Promise.all([
      API.get('/artikels').catch(() => ({ data: [] })),
      API.get('/leveranciers').catch(() => ({ data: [] })),
      API.get('/artikel-params').catch(() => ({ data: {} })),
    ]).then(([arts, levs, params]) => {
      const cats = [...new Set((arts.data || []).map(a => a.categorie).filter(Boolean))].sort();
      setCategorieen(cats);
      setLeveranciers(levs.data || []);
      setPrijsgroepen(params.data?.groepen || []);
    });
  }, [open]);

  if (!open) return null;

  const toggleGroep = (key) => {
    setGroepenAanpassen(p => p.includes(key) ? p.filter(k => k !== key) : [...p, key]);
  };

  const berekenPreview = async () => {
    setErr('');
    if (groepenAanpassen.length === 0) { setErr('Kies minstens 1 prijsgroep.'); return; }
    if (!percentage || percentage <= 0) { setErr('Percentage moet > 0 zijn.'); return; }
    const pct = richting === 'op' ? percentage : -percentage;
    setBusy(true);
    try {
      const r = await API.post('/artikels/prijs-wizard', {
        percentage: pct,
        prijsgroepen: groepenAanpassen,
        filter_categorie: filterCategorie,
        filter_leverancier: filterLeverancier,
        afronding,
        min_marge_pct: minMarge,
        inclusief_aankoop: inclAankoop,
        fallback_van: afleidenActief ? fallbackVan : '',
        fallback_factoren: afleidenActief ? fallbackFactoren : {},
      });
      setPreview(r.data);
      setStap(2);
    } catch (e) {
      setErr(e.response?.data?.detail || 'Berekening mislukt');
    } finally { setBusy(false); }
  };

  const stuurNaarImport = () => {
    if (!preview?.csv_voor_import) return;
    if (onStuurNaarImport) onStuurNaarImport(preview.csv_voor_import);
    onClose();
  };

  const downloadCsv = () => {
    if (!preview?.csv_voor_import) return;
    const blob = new Blob([preview.csv_voor_import], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const sign = preview.percentage >= 0 ? '+' : '';
    a.download = `prijswijziging-${sign}${preview.percentage}pct-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const reset = () => { setStap(1); setPreview(null); setErr(''); };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-3" onClick={() => !busy && onClose()} data-testid="prijs-wizard-modal">
      <div className="w-full max-w-4xl bg-white shadow-2xl flex flex-col" style={{ maxHeight: '95vh' }} onClick={e => e.stopPropagation()}>
        <div className="px-5 py-3 bg-[#059669] text-white flex items-center justify-between">
          <h3 className="font-bold text-base inline-flex items-center gap-2">
            <Calculator size={18} weight="fill" /> Prijs-verhogingswizard
            <span className="text-[10px] bg-white/20 px-2 py-0.5 uppercase tracking-wider">Stap {stap}/2</span>
          </h3>
          <button onClick={onClose} disabled={busy} className="p-1.5 hover:bg-white/10" aria-label="Prijswizard sluiten" data-testid="prijs-wizard-close"><X size={18} weight="bold" /></button>
        </div>

        <div className="flex-1 overflow-y-auto">
          {/* STAP 1 — Configuratie */}
          {stap === 1 && (
            <div className="p-5 space-y-4">
              {/* Richting + percentage */}
              <div>
                <div className="text-[10px] uppercase font-bold tracking-wider text-gray-600 mb-1">Actie</div>
                <div className="flex items-center gap-3 flex-wrap">
                  <div className="flex gap-1">
                    <button
                      onClick={() => setRichting('op')}
                      className={`px-4 py-2 text-sm font-bold inline-flex items-center gap-1.5 border-2 ${richting === 'op' ? 'bg-green-600 text-white border-green-600' : 'bg-white text-gray-600 border-gray-200'}`}
                      data-testid="wizard-richting-op"
                    >
                      <TrendUp size={14} weight="bold" /> Verhogen
                    </button>
                    <button
                      onClick={() => setRichting('neer')}
                      className={`px-4 py-2 text-sm font-bold inline-flex items-center gap-1.5 border-2 ${richting === 'neer' ? 'bg-red-600 text-white border-red-600' : 'bg-white text-gray-600 border-gray-200'}`}
                      data-testid="wizard-richting-neer"
                    >
                      <TrendDown size={14} weight="bold" /> Verlagen
                    </button>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <input type="number" step="0.1" min="0" max="100" className="field-input font-mono text-center font-bold text-lg" style={{ width: 100 }} value={percentage} onChange={e => setPercentage(Number(e.target.value))} data-testid="wizard-percentage" />
                    <span className="text-lg font-bold text-gray-600">%</span>
                  </div>
                  <div className="text-xs text-gray-500 italic">
                    → <strong>{richting === 'op' ? '+' : '-'}{percentage}%</strong> op de geselecteerde prijsgroepen
                  </div>
                </div>
              </div>

              {/* Prijsgroepen */}
              <div>
                <div className="text-[10px] uppercase font-bold tracking-wider text-gray-600 mb-1">Prijsgroepen om aan te passen</div>
                <div className="flex flex-wrap gap-2">
                  {prijsgroepen.map(g => (
                    <button
                      key={g.key}
                      onClick={() => toggleGroep(g.key)}
                      className={`px-3 py-1.5 text-sm font-bold border-2 inline-flex items-center gap-1.5 ${groepenAanpassen.includes(g.key) ? 'text-white' : 'bg-white text-gray-600 border-gray-200'}`}
                      style={groepenAanpassen.includes(g.key) ? { background: g.color, borderColor: g.color } : {}}
                      data-testid={`wizard-groep-${g.key}`}
                    >
                      {groepenAanpassen.includes(g.key) && <CheckCircle size={12} weight="fill" />}
                      {g.label}
                    </button>
                  ))}
                </div>
                <label className="inline-flex items-center gap-2 text-xs font-bold cursor-pointer mt-2">
                  <input type="checkbox" checked={inclAankoop} onChange={e => setInclAankoop(e.target.checked)} data-testid="wizard-incl-aankoop" />
                  Ook aankoopprijs aanpassen (bv. leveranciers-indexatie)
                </label>
              </div>

              {/* Ontbrekende prijsgroepen afleiden */}
              <div className="border-2 border-dashed border-amber-300 bg-amber-50/40 p-3">
                <label className="inline-flex items-center gap-2 text-xs font-bold cursor-pointer">
                  <input
                    type="checkbox"
                    checked={afleidenActief}
                    onChange={e => setAfleidenActief(e.target.checked)}
                    data-testid="wizard-afleiden-actief"
                  />
                  Ontbrekende prijsgroepen automatisch afleiden uit bron-groep
                </label>
                <div className="text-[11px] text-amber-900/80 mt-1 ml-6">
                  Voor artikels waar een geselecteerde prijsgroep nog leeg is, wordt de huidige waarde berekend als <strong>bron × factor%</strong>, en daarna het wijzigings-percentage toegepast. Zo bouwt u in één actie Standard / Premium / Bulk / Kassa op vanuit enkel Basic.
                </div>
                {afleidenActief && (
                  <div className="mt-3 ml-6 space-y-2">
                    <div className="flex items-center gap-2">
                      <label className="text-[10px] uppercase font-bold tracking-wider text-gray-600 w-24">Bron-groep</label>
                      <select
                        className="field-input font-bold"
                        style={{ maxWidth: 180 }}
                        value={fallbackVan}
                        onChange={e => setFallbackVan(e.target.value)}
                        data-testid="wizard-fallback-van"
                      >
                        {prijsgroepen.map(g => <option key={g.key} value={g.key}>{g.label}</option>)}
                      </select>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                      {prijsgroepen.filter(g => g.key !== fallbackVan).map(g => (
                        <div key={g.key} className="flex items-center gap-1.5">
                          <span className="text-[11px] font-bold w-16 truncate" style={{ color: g.color }}>{g.label}</span>
                          <div className="relative flex-1">
                            <input
                              type="number"
                              step="1"
                              min="0"
                              max="500"
                              className="field-input pr-8 text-right font-mono"
                              value={fallbackFactoren[g.key] ?? ''}
                              onChange={e => setFallbackFactoren(p => ({ ...p, [g.key]: e.target.value === '' ? '' : Number(e.target.value) }))}
                              placeholder="-"
                              data-testid={`wizard-factor-${g.key}`}
                            />
                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs font-bold">%</span>
                          </div>
                        </div>
                      ))}
                      {/* Kassa staat vaak niet in de hoofd-groepen, apart tonen */}
                      {!prijsgroepen.some(g => g.key === 'kassa') && fallbackVan !== 'kassa' && (
                        <div className="flex items-center gap-1.5">
                          <span className="text-[11px] font-bold w-16 truncate text-rose-600">Kassa</span>
                          <div className="relative flex-1">
                            <input
                              type="number"
                              step="1"
                              min="0"
                              max="500"
                              className="field-input pr-8 text-right font-mono"
                              value={fallbackFactoren.kassa ?? ''}
                              onChange={e => setFallbackFactoren(p => ({ ...p, kassa: e.target.value === '' ? '' : Number(e.target.value) }))}
                              data-testid="wizard-factor-kassa"
                            />
                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 text-xs font-bold">%</span>
                          </div>
                        </div>
                      )}
                    </div>
                    <div className="text-[10px] text-gray-500 italic">Voorbeeld: 95% → afgeleide prijs = bron × 0,95. Leeg laten = die groep niet afleiden.</div>
                  </div>
                )}
              </div>

              {/* Filters */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase font-bold tracking-wider text-gray-600 block mb-1">Filter: enkel categorie</label>
                  <select className="field-input" value={filterCategorie} onChange={e => setFilterCategorie(e.target.value)} data-testid="wizard-filter-cat">
                    <option value="">Alle categorieën</option>
                    {categorieen.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase font-bold tracking-wider text-gray-600 block mb-1">Filter: enkel leverancier</label>
                  <select className="field-input" value={filterLeverancier} onChange={e => setFilterLeverancier(e.target.value)} data-testid="wizard-filter-lev">
                    <option value="">Alle leveranciers</option>
                    {leveranciers.map(l => <option key={l.id} value={l.id}>{l.bedrijf || l.naam || l.id}</option>)}
                  </select>
                </div>
              </div>

              {/* Afronding + marge */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] uppercase font-bold tracking-wider text-gray-600 block mb-1">Afronding nieuwe prijs</label>
                  <select className="field-input" value={afronding} onChange={e => setAfronding(e.target.value)} data-testid="wizard-afronding">
                    <option value="geen">Geen (2 decimalen)</option>
                    <option value="nearest_0.05">Naar dichtstbijzijnde €0.05</option>
                    <option value="nearest_0.99">Naar €X.99 (consumenten-trucje)</option>
                    <option value="integer">Hele euro's</option>
                  </select>
                </div>
                <div>
                  <label className="text-[10px] uppercase font-bold tracking-wider text-gray-600 block mb-1">Min. marge-bescherming</label>
                  <div className="relative">
                    <input type="number" step="1" min="0" max="500" className="field-input pr-10" value={minMarge} onChange={e => setMinMarge(Number(e.target.value))} placeholder="0 = uit" data-testid="wizard-marge" />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-bold">%</span>
                  </div>
                  <div className="text-[10px] text-gray-500 mt-0.5">Skip artikels waar verkoop &lt; aankoop × (1 + marge%)</div>
                </div>
              </div>

              {err && <div className="px-3 py-2 bg-red-50 border border-red-200 text-xs text-red-800 font-bold">{err}</div>}

              <div className="flex gap-2 pt-2 border-t border-gray-200">
                <button onClick={berekenPreview} disabled={busy || groepenAanpassen.length === 0} className="btn btn-primary font-bold inline-flex items-center gap-2" style={{ background: '#059669', borderColor: '#059669' }} data-testid="wizard-bereken">
                  <Calculator size={14} weight="bold" /> {busy ? 'Bezig...' : 'Preview berekenen'}
                </button>
                <button onClick={onClose} className="btn btn-secondary">Annuleren</button>
              </div>
            </div>
          )}

          {/* STAP 2 — Preview */}
          {stap === 2 && preview && (
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                <KPI label="Artikels" value={preview.totaal} color="#059669" />
                <KPI label="Wijziging" value={`${preview.percentage >= 0 ? '+' : ''}${preview.percentage}%`} color={preview.percentage >= 0 ? '#059669' : '#DC2626'} />
                <KPI label="Groepen" value={(preview.groepen || []).length} color="#002FA7" />
                <KPI label="Afronding" value={preview.afronding === 'geen' ? '2 dec.' : preview.afronding.replace('_', ' ')} color="#6B7280" />
                <KPI
                  label="Afgeleid"
                  value={(preview.preview || []).filter(p => (p.afgeleide_groepen || []).length > 0).length}
                  color="#D97706"
                />
              </div>

              {preview.totaal === 0 && (
                <div className="p-4 border border-amber-200 bg-amber-50 inline-flex items-start gap-2 w-full">
                  <Warning size={18} className="text-amber-600 shrink-0 mt-0.5" weight="fill" />
                  <div className="text-xs text-amber-800"><strong>Geen artikels gevonden</strong> die aan uw criteria voldoen. Pas filters aan of deselecteer de marge-bescherming.</div>
                </div>
              )}

              {preview.totaal > 0 && (
                <div className="border border-gray-200 overflow-auto max-h-80">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr className="text-[10px] uppercase tracking-wider text-gray-600 font-bold">
                        <th className="text-left px-2 py-1.5">Art.code</th>
                        <th className="text-left px-2 py-1.5">Naam</th>
                        <th className="text-left px-2 py-1.5">Cat.</th>
                        {(preview.groepen || []).map(g => (
                          <th key={g} className="text-right px-2 py-1.5 capitalize">{g}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {preview.preview.map((p, i) => (
                        <tr key={p.art_code + i} className="border-t border-gray-100 hover:bg-gray-50" data-testid={`wizard-preview-${p.art_code}`}>
                          <td className="px-2 py-1.5 font-mono">
                            {p.art_code}
                            {(p.afgeleide_groepen || []).length > 0 && (
                              <span
                                className="ml-1.5 inline-block px-1.5 py-0.5 text-[9px] uppercase tracking-wider font-bold bg-amber-100 text-amber-800 border border-amber-300"
                                title={`Afgeleid uit bron-groep: ${(p.afgeleide_groepen || []).join(', ')}`}
                                data-testid={`wizard-afgeleid-${p.art_code}`}
                              >
                                afgeleid
                              </span>
                            )}
                          </td>
                          <td className="px-2 py-1.5 font-bold truncate max-w-[200px]">{p.naam}</td>
                          <td className="px-2 py-1.5 text-gray-500">{p.categorie || '-'}</td>
                          {(preview.groepen || []).map(g => {
                            const oud = p.prijzen_huidig[g];
                            const nw = p.prijzen_nieuw[g];
                            const isAfgeleid = (p.afgeleide_groepen || []).includes(g);
                            return (
                              <td key={g} className="px-2 py-1.5 text-right font-mono">
                                {nw ? (
                                  <div>
                                    <span className={`line-through text-[10px] ${isAfgeleid ? 'text-amber-600 italic' : 'text-gray-400'}`}>
                                      {oud ? fmt(oud) : '-'}
                                      {isAfgeleid && <span className="ml-0.5">*</span>}
                                    </span>
                                    <span className="ml-1 font-bold" style={{ color: preview.percentage >= 0 ? '#059669' : '#DC2626' }}>{fmt(nw)}</span>
                                  </div>
                                ) : <span className="text-gray-300">-</span>}
                              </td>
                            );
                          })}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {preview.totaal > 0 && (preview.preview || []).some(p => (p.afgeleide_groepen || []).length > 0) && (
                <div className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 px-3 py-2 inline-flex items-start gap-2">
                  <Warning size={14} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
                  <div>
                    <strong>*</strong> = huidige waarde was leeg en werd berekend uit de bron-groep <strong>{preview.fallback_van || 'basic'}</strong> × de door u ingestelde factor. Na toepassen is de hele prijsgroep (basic + afgeleiden) beschikbaar op uw artikels.
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {stap === 2 && preview && (
          <div className="px-5 py-3 border-t border-gray-200 bg-gray-50 flex items-center justify-between gap-2">
            <button onClick={reset} className="btn btn-secondary inline-flex items-center gap-1.5" data-testid="wizard-terug">
              ← Terug naar config
            </button>
            <div className="flex gap-2">
              <button onClick={downloadCsv} disabled={preview.totaal === 0} className="btn btn-secondary inline-flex items-center gap-1.5" data-testid="wizard-download-csv">
                Download CSV
              </button>
              <button onClick={stuurNaarImport} disabled={preview.totaal === 0} className="btn btn-primary font-bold inline-flex items-center gap-2" data-testid="wizard-naar-import">
                Doorsturen naar AI Import <ArrowRight size={14} weight="bold" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function KPI({ label, value, color }) {
  return (
    <div className="border-l-4 p-2.5 bg-white" style={{ borderColor: color }}>
      <div className="text-[10px] uppercase font-bold tracking-wider text-gray-500">{label}</div>
      <div className="text-xl font-bold mt-0.5 font-mono" style={{ color }}>{value}</div>
    </div>
  );
}
